import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, MapPin, ChevronDown, FileText, Users, Calculator, TrendingUp, Building2, CheckCircle } from 'lucide-react';

export default function StudioPenulaWebsite() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
      setActiveSection(id);
    }
  };

  const services = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Collaboratore a Domicilio",
      description: "Per le imprese che necessitano di gestione amministrativa in sede, garantiamo la presenza di una collaboratrice dello Studio per fatturazione, adempimenti bancari e rapporti con clienti/fornitori.",
      highlight: true
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Servizi alle Imprese",
      description: "Tenuta contabilità, formazione bilanci, dichiarazioni redditi, assistenza tributaria, gestione agenti di commercio, rapporti con Enti Previdenziali, IMU/TASI.",
      details: ["Contabilità ordinaria e semplificata", "Bilanci d'esercizio", "Dichiarazioni tributarie", "Gestione Enasarco"]
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Servizi ai Privati",
      description: "Dichiarazioni dei redditi (Mod. Unico e 730), registrazione contratti di locazione, IMU/TASI, domande pensione reversibilità, istanze dilazioni amministrative.",
      details: ["Mod. Unico e 730", "Contratti locazione", "Pratiche pensionistiche"]
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Consulenze Start-up",
      description: "Consulenza professionale per avvio nuove attività, formazione budget economico/finanziario, supporto completo per le imprese nascenti.",
      details: ["Business plan", "Budget economici", "Supporto Start-up"]
    },
    {
      icon: <Calculator className="w-8 h-8" />,
      title: "Consulenza Societaria",
      description: "Operazioni straordinarie, perizie societarie, valutazioni d'impresa, assistenza in contenzioso tributario e consulenza aziendale strategica.",
      details: ["Operazioni straordinarie", "Perizie e valutazioni", "Contenzioso tributario"]
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Consulenza Contrattualistica",
      description: "Consulenza in materia contrattualistica, formazione e revisione contratti, assistenza nella definizione di accordi commerciali e societari.",
      details: ["Redazione contratti", "Revisione legale", "Accordi commerciali"]
    }
  ];

  const links = [
    { name: "Agenzia delle Entrate", url: "https://www.agenziaentrate.gov.it" },
    { name: "Camera di Commercio", url: "https://www.camcom.it" },
    { name: "Comune di Milano", url: "https://www.comune.milano.it" },
    { name: "INAIL", url: "https://www.inail.it" },
    { name: "INPS", url: "https://www.inps.it" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-lg shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center shadow-lg">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                  Studio Penula
                </h1>
                <p className="text-xs text-gray-600">Datagest SAS</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              {['home', 'datagest', 'servizi', 'staff', 'contatti'].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className={`text-sm font-medium transition-colors ${
                    activeSection === section 
                      ? 'text-blue-600' 
                      : 'text-gray-700 hover:text-blue-600'
                  }`}
                >
                  {section === 'home' ? 'Home' : section === 'datagest' ? 'DataGest' : section === 'servizi' ? 'Servizi' : section === 'staff' ? 'Staff' : 'Contatti'}
                </button>
              ))}
            </nav>

            {/* Right side buttons */}
            <div className="flex items-center space-x-4">
              <a
                href="https://cloud.studiopenula.it"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
                <span>Area Clienti</span>
              </a>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t shadow-lg">
            <div className="px-4 py-4 space-y-3">
              {['home', 'datagest', 'servizi', 'staff', 'contatti'].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className="block w-full text-left px-4 py-2 rounded-lg hover:bg-blue-50 text-gray-700 font-medium"
                >
                  {section === 'home' ? 'Home' : section === 'datagest' ? 'DataGest' : section === 'servizi' ? 'Servizi' : section === 'staff' ? 'Staff' : 'Contatti'}
                </button>
              ))}
              <a
                href="https://cloud.studiopenula.it"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg font-semibold shadow-md"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
                <span>Area Clienti</span>
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-8">
            <div className="space-y-4 animate-fade-in">
              <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
                La tua consulenza
                <span className="block bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                  fiscale e societaria
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Un punto di riferimento per imprenditori e professionisti che cercano una consulenza globale per l'amministrazione della propria attività.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => scrollToSection('contatti')}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              >
                Contattaci Ora
              </button>
              <button
                onClick={() => scrollToSection('servizi')}
                className="px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 border-2 border-blue-600"
              >
                I Nostri Servizi
              </button>
              <a
                href="https://cloud.studiopenula.it"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
                <span>Area Clienti</span>
              </a>
            </div>

            <div className="mt-12 animate-bounce">
              <ChevronDown className="w-8 h-8 text-blue-600 mx-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* DataGest Section */}
      <section id="datagest" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-blue-500/20 rounded-full text-blue-200 text-sm font-semibold mb-4">
                Dal 1988
              </div>
              <h3 className="text-4xl font-bold mb-6">DataGest SAS</h3>
              <div className="space-y-4 text-lg text-blue-100 leading-relaxed">
                <p>
                  Opera dal <strong className="text-white">1988</strong> a San Giuliano Milanese direttamente a contatto di imprese individuali, di professionisti e di società.
                </p>
                <p>
                  Vanta una <strong className="text-white">consolidata e comprovata esperienza</strong> per esigenze di tipo contabile, fiscale e amministrativo.
                </p>
                <p>
                  Grazie all'utilizzo dei <strong className="text-white">più avanzati strumenti informatici</strong> e ad un organico completo, frutto dell'accurata selezione e formazione dei collaboratori, è in grado di fornire servizi di alta qualità.
                </p>
                <p className="text-blue-200 font-medium">
                  Per le consulenze professionali si avvale della collaborazione dello Studio Penula.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <div className="text-5xl font-bold text-blue-300 mb-2">35+</div>
                <div className="text-blue-100">Anni di esperienza</div>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <div className="text-5xl font-bold text-blue-300 mb-2">100%</div>
                <div className="text-blue-100">Clienti soddisfatti</div>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <div className="text-5xl font-bold text-blue-300 mb-2">24/7</div>
                <div className="text-blue-100">Supporto disponibile</div>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <div className="text-5xl font-bold text-blue-300 mb-2">+500</div>
                <div className="text-blue-100">Aziende servite</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servizi" className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">I Nostri Servizi</h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Soluzioni complete e personalizzate per ogni esigenza aziendale
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 border ${
                  service.highlight 
                    ? 'border-green-400 ring-4 ring-green-100' 
                    : 'border-gray-100'
                }`}
              >
                {service.highlight && (
                  <div className="inline-block px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold rounded-full mb-4">
                    ⭐ NOVITÀ
                  </div>
                )}
                <div className={`w-16 h-16 bg-gradient-to-br ${
                  service.highlight 
                    ? 'from-green-100 to-emerald-200 text-green-600' 
                    : 'from-blue-100 to-blue-200 text-blue-600'
                } rounded-xl flex items-center justify-center mb-6`}>
                  {service.icon}
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h4>
                <p className="text-gray-600 leading-relaxed mb-4">{service.description}</p>
                {service.details && (
                  <ul className="space-y-2">
                    {service.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm text-gray-500">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Staff Section */}
      <section id="staff" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Il Nostro Staff</h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Un team di professionisti esperti al servizio della tua azienda
            </p>
          </div>

          {/* Director */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-3xl p-8 md:p-12 shadow-2xl mb-12 border-4 border-blue-400">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center shadow-2xl flex-shrink-0">
                <Users className="w-16 h-16 text-blue-600" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h4 className="text-3xl font-bold text-white mb-2">Dott. Gianmario Penula</h4>
                <p className="text-xl text-blue-200 font-semibold mb-4">Titolare - Dottore Commercialista</p>
                <p className="text-blue-100 leading-relaxed mb-3">
                  Iscritto all'Ordine dei Dottori Commercialisti ed Esperti Contabili di Milano
                </p>
                <a 
                  href="mailto:g.penula@studiopenula.it"
                  className="inline-flex items-center space-x-2 text-white bg-blue-500/30 hover:bg-blue-500/50 px-4 py-2 rounded-lg transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  <span>g.penula@studiopenula.it</span>
                </a>
              </div>
            </div>
          </div>

          {/* Team Members */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              { name: "Dott.ssa Chiara Penula", email: "chiarapenula.data@studiopenula.it", role: "Contabilità" },
              { name: "Alessandra Lamperti", email: "lamperti.data@studiopenula.it", role: "Contabilità" },
              { name: "Giada Stefanini", email: "stefanini.data@studiopenula.it", role: "Contabilità" },
              { name: "Alice Affaticati", email: "affaticati.data@studiopenula.it", role: "Contabilità" },
              { name: "Jessica Bailini", email: "bailini.data@studiopenula.it", role: "Contabilità" }
            ].map((member, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 border border-gray-100"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-blue-600" />
                </div>
                <h5 className="text-xl font-bold text-gray-900 text-center mb-2">
                  {member.name}
                </h5>
                <p className="text-blue-600 text-center font-medium mb-4 text-sm">
                  {member.role}
                </p>
                <a 
                  href={`mailto:${member.email}`}
                  className="flex items-center justify-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors text-sm bg-gray-50 hover:bg-blue-50 px-3 py-2 rounded-lg"
                >
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{member.email}</span>
                </a>
              </div>
            ))}
          </div>

          {/* Team Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h5 className="text-xl font-bold text-gray-900 mb-2">Collaboratori Qualificati</h5>
                  <p className="text-gray-600 leading-relaxed">
                    La struttura dello Studio è snella ed efficiente, dotata di collaboratori qualificati e personale con pluriennale esperienza nel settore.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h5 className="text-xl font-bold text-gray-900 mb-2">Formazione Continua</h5>
                  <p className="text-gray-600 leading-relaxed">
                    Il team è costantemente aggiornato sulle normative fiscali e tributarie per garantire un servizio sempre all'avanguardia.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="chi-siamo" className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h3 className="text-4xl font-bold text-gray-900">La Nostra Missione</h3>
              <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
                <p>
                  Lo <strong>Studio Penula</strong> vuole essere il punto di riferimento per le piccole e medie imprese che intendono far gestire anche all'esterno, ma in totale sicurezza ed efficienza, l'elaborazione dei dati contabili.
                </p>
                <p>
                  Affidiamo alla competenza ed all'esperienza di professionisti seri la consulenza in materia tributaria.
                </p>
                <p>
                  Le aziende che scelgono di affidarsi allo Studio Penula avviano un rapporto mirato alla <strong>totale soddisfazione</strong> delle proprie esigenze.
                </p>
              </div>

              <div className="space-y-3 pt-6">
                {['Esperienza pluriennale', 'Approccio personalizzato', 'Aggiornamento continuo', 'Massima affidabilità'].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-3xl p-12 shadow-2xl transform rotate-3">
                <div className="bg-white rounded-2xl p-8 transform -rotate-3 shadow-xl">
                  <h4 className="text-2xl font-bold text-gray-900 mb-6">Link Utili</h4>
                  <div className="space-y-3">
                    {links.map((link, idx) => (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-3 bg-gray-50 hover:bg-blue-50 rounded-lg transition-colors text-gray-700 hover:text-blue-600 font-medium"
                      >
                        {link.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Area Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-white">
              <div className="inline-block px-4 py-2 bg-white/20 rounded-full text-sm font-semibold mb-4">
                🔒 Accesso Sicuro
              </div>
              <h3 className="text-4xl md:text-5xl font-bold leading-tight">
                Area Clienti Cloud
              </h3>
              <p className="text-xl text-blue-100 leading-relaxed">
                Accedi alla tua area riservata per consultare e condividere documenti in modo sicuro e veloce. Disponibile 24/7 da qualsiasi dispositivo.
              </p>
              
              <div className="space-y-4 pt-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-lg mb-1">Condivisione Documenti</h5>
                    <p className="text-blue-100">Carica e scarica documenti in modo sicuro</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-lg mb-1">Accesso 24/7</h5>
                    <p className="text-blue-100">Disponibile sempre, da ovunque tu sia</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-lg mb-1">Massima Sicurezza</h5>
                    <p className="text-blue-100">Crittografia e protezione dei tuoi dati</p>
                  </div>
                </div>
              </div>

              <a
                href="https://cloud.studiopenula.it"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-3 px-8 py-4 bg-white text-blue-700 rounded-xl font-bold shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 mt-8"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
                <span>Accedi all'Area Clienti</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
            </div>

            <div className="relative">
              <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/30 shadow-2xl">
                <div className="space-y-4">
                  <div className="flex items-center space-x-4 p-4 bg-white/10 rounded-xl">
                    <div className="w-12 h-12 bg-blue-400 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="h-3 bg-white/30 rounded w-3/4 mb-2"></div>
                      <div className="h-2 bg-white/20 rounded w-1/2"></div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 p-4 bg-white/10 rounded-xl">
                    <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="h-3 bg-white/30 rounded w-2/3 mb-2"></div>
                      <div className="h-2 bg-white/20 rounded w-1/3"></div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 p-4 bg-white/10 rounded-xl">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="h-3 bg-white/30 rounded w-4/5 mb-2"></div>
                      <div className="h-2 bg-white/20 rounded w-2/5"></div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 text-center">
                  <p className="text-white/80 text-sm">
                    Interfaccia intuitiva per la gestione dei tuoi documenti
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contatti" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-white mb-4">Contattaci</h3>
            <p className="text-xl text-blue-100">Siamo qui per aiutarti a far crescere la tua attività</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center border border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-2">Telefono</h4>
              <a href="tel:0298444845" className="text-blue-100 hover:text-white transition-colors">
                02.98 44845
              </a>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center border border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-2">Email</h4>
              <a href="mailto:datagest@studiopenula.it" className="text-blue-100 hover:text-white transition-colors break-all">
                datagest@studiopenula.it
              </a>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center border border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-2">Indirizzo</h4>
              <p className="text-blue-100">
                Via F. Baracca 7<br />
                San Giuliano Milanese<br />
                20098 Milano
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold">Studio Penula</span>
          </div>
          <p className="text-gray-400 mb-2">Datagest SAS - Consulenza Fiscale e Societaria</p>
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Studio Penula. Tutti i diritti riservati.
          </p>
        </div>
      </footer>
    </div>
  );
}